/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FC, useEffect, useRef, useState } from "react";
import {
  addCertificate,
  updateCertificate,
  viewCertificate,
} from "../core/_requests";
import { useAuth } from "../../../auth";

import {
  Cerificate,
  initialValues as initialFormValues,
} from "../core/_models";
import clsx from "clsx";
import { faUserCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { certificateSchema } from "../core/Schema";
type Props = {
  initialValues: Cerificate;
};

const EditCertificate: FC<Props> = ({ initialValues }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Cerificate>(initialValues);
  const [errorMessage, setErrorMessage] = useState("");
  const [formImage, setFormImage] = useState<File | null>(null);
  const chefId = currentUser?.uuid;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { id } = useParams();



  useEffect(() => {
    if (id) {
      viewCertificate(chefId)
        .then((res) => {
          if (res?.data) {
            setData(res?.data);
            if (res?.data?.certificate) {
             

              // Create a "file-like" blob to handle the initial image/file preview if it exists
              const blob = new Blob([res.data.certificate], {
                type: "image/jpeg",
              }); // adjust type as needed
              const file = new File([blob], "initial_certificate.jpg", {
                type: "image/jpeg",
              });
              setFormImage(file); // Set the file object
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching certificate:", error);
        });
    }
  }, [chefId, id]);

  const clickCancel = () => {
    navigate("/settings");
  };

  // Formik configuration
  const formik = useFormik<Cerificate>({
    initialValues: data, // Use `data` to set the initial values dynamically
    validationSchema: certificateSchema,
    enableReinitialize: true, // Reinitialize form when data is fetched
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      const updatedData = Object.assign(data, values);
      values.chef_id = chefId;

      const formData = new FormData();
      formData.append("name", values.name ?? "");
      formData.append("description", values.description ?? "");
      formData.append("chef_id", values.chef_id ?? "");
      if (formImage) formData.append("certificate", formImage);

      updateCertificate(id, formData).then((res) => {
        setLoading(false);
        if (res.status === true) {
          navigate("/settings");
          toast.success(res?.message, { autoClose: 5000 });
        } else {
          toast.error(res?.message);
          // Redirect to the settings page
          navigate("/settings");
        }
      });
    },
  });

  // Image handler
  const imageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFormImage(files[0]); // Store the file
      const reader = new FileReader();
      reader.onload = () => {
        setData({
          ...data,
          certificate: reader.result as string, // Preview the uploaded file (image/pdf)
        });
      };
      reader.readAsDataURL(files[0]); // Generate preview for images (jpg, png)
    }
  };

  const handleRemoveImage = () => {
    setFormImage(null); // Remove the form image
    setData({ ...data, certificate: "" }); // Clear the certificate field
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset the file input
    }
  };

  const handleErrorMessageClose = () => {
    setErrorMessage("");
  };

  return (
    <section className="container-fluid">
      <div className="row">
        <section className="content-section">
          <div className="overlay"></div>
          <div className="drawer small">
            <div className="title row">
              <h3 className="col-10 mb-0">Certificate</h3>
              <div className="col-2 text-end mb-2">
                <button
                  className="btn p-0 close d-inline-flex"
                  onClick={clickCancel}
                >
                  <i className="material-symbols-outlined">close</i>
                </button>
              </div>
              <h6>Upload your course completion certificate here!</h6>
            </div>
            <form
              onSubmit={formik.handleSubmit}
              noValidate
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            >
              <div className="col-12 mb-3 mt-4">
                <label htmlFor="name" className="form-label">
                  Certificate name / title
                </label>
                <input
                  type="text"
                  className={clsx("form-control", {
                    "is-invalid": formik.touched.name && formik.errors.name,
                    "is-valid": formik.touched.name && !formik.errors.name,
                  })}
                  placeholder="Enter certificate name / title"
                  {...formik.getFieldProps("name")}
                />
              </div>
              <div className="col-12 mb-3">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <textarea
                  className={clsx("form-control textarea", {
                    "is-invalid":
                      formik.touched.description && formik.errors.description,
                    "is-valid":
                      formik.touched.description && !formik.errors.description,
                  })}
                  placeholder="Enter a short description..."
                  {...formik.getFieldProps("description")}
                  cols={30}
                  rows={10}
                ></textarea>
              </div>

              <div className="col-12 mb-3 mt-3">
                <label className="form-label fs-6">Upload Certificate</label>
                <div className="file-input">
                  <input
                    type="file"
                    onChange={imageHandler}
                    ref={fileInputRef}
                    accept=".jpg, .jpeg, .png, .webp, .pdf, .doc, .docx"
                  />
                  <label htmlFor="file">
                    <i className="material-symbols-outlined">upload</i>
                    <span>Upload Certificate</span>
                  </label>
                </div>
                <span className="file-info">
                  Supported file types: .jpg, .png, .jpeg, .webp, .pdf, .doc,
                  .docx. Max size: 1MB
                </span>

                {/* Preview uploaded file or show default icon */}
                {formImage || data?.certificate ? (
                  <div className="mt-2 d-flex align-items-center">
                    {/* If it's an image file */}
                    {formImage && formImage.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(formImage)}
                        alt="certificate-preview"
                        width="100"
                        height="100"
                        className="image-preview shadow rounded"
                      />
                    ) : formImage?.type === "application/pdf" ||
                      formImage?.name.endsWith(".pdf") ? (
                      <iframe
                        src={URL.createObjectURL(formImage)}
                        title="certificate-preview"
                        width="200"
                        height="200"
                      />
                    ) : formImage?.name.endsWith(".doc") ||
                      formImage?.name.endsWith(".docx") ? (
                      <a
                        href={URL.createObjectURL(formImage)}
                        download={formImage.name}
                      >
                        Download {formImage.name}
                      </a>
                    ) : data?.certificate &&
                      data.certificate.startsWith("https") ? (
                      // If the certificate is already saved
                      data.certificate.endsWith(".pdf") ? (
                        <iframe
                          src={data.certificate}
                          title="certificate-preview"
                          width="200"
                          height="200"
                        />
                      ) : data.certificate.endsWith(".doc") ||
                        data.certificate.endsWith(".docx") ? (
                        <a href={data.certificate} download>
                          Download {data.certificate}
                        </a>
                      ) : (
                        <img
                          src={data.certificate}
                          alt="certificate-preview"
                          width="100"
                          height="100"
                          className="image-preview shadow rounded"
                        />
                      )
                    ) : (
                      <span>{formImage?.name || data?.certificate}</span>
                    )}

                    {/* Delete button for removing the image */}
                    <i
                      className="material-symbols-outlined ms-2 text-danger cursor-pointer"
                      onClick={handleRemoveImage}
                      title="Remove certificate"
                    >
                      delete
                    </i>
                  </div>
                ) : (
                  <FontAwesomeIcon
                    icon={faUserCircle}
                    className="user-icon"
                    size="3x"
                  />
                )}
              </div>

              <div className="col-12 mt-3">
                <div className="row">
                  <div className="col-8 text-end">
                    <button
                      className="btn btn-cancel rounded-5"
                      onClick={clickCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !formik.isValid}
                      className="btn btn-outline-primary rounded-5 me-2"
                    >
                      {!loading && (
                        <span className="indicator-label">Publish</span>
                      )}
                      {loading && (
                        <span
                          className="indicator-progress"
                          style={{ display: "block" }}
                        >
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </section>
      </div>
    </section>
  );
};

export { EditCertificate };
